import React from 'react';
import { TEXT_INPUT_SUBTYPES } from '../../surveyutilities/surveyStore';
import {
    PropsShell,
    PropsNotFound,
    PropsCommonFields,
    PropsSection,
    PropsField,
    PropsRequiredAndMeta,
    useQuestionProperties,
} from './QuestionPropertiesShared';

const formatInputType = (subType) => {
    switch (subType) {
        case 'text':
            return 'Text';
        case 'email':
            return 'Email';
        case 'password':
            return 'Password';
        case 'url':
            return 'URL';
        case 'tel':
            return 'Telephone';
        case 'number':
            return 'Number';
        case 'range':
            return 'Range';
        case 'color':
            return 'Color';
        case 'date':
            return 'Date';
        case 'time':
            return 'Time';
        case 'datetime-local':
            return 'Date and time';
        case 'week':
            return 'Week';
        case 'month':
            return 'Month';
        default:
            return 'Text';
    }
};

const TextBlockProperties = ({ questionId }) => {
    const { question, localName, nameError, handleUpdate, handleNameChange } =
        useQuestionProperties(questionId);

    if (!question) return <PropsNotFound />;

    const inputType = question.inputType || 'text';

    return (
        <PropsShell typeLabel="Single-line text" typeIcon="bi-input-cursor-text">
            <PropsCommonFields
                localName={localName}
                nameError={nameError}
                onNameChange={handleNameChange}
                title={question.title}
                description={question.description}
                onTitleChange={(value) => handleUpdate('title', value)}
                onDescriptionChange={(value) => handleUpdate('description', value)}
            />

            <PropsSection title="Input">
                <PropsField label="Input type">
                    <select
                        className="form-select form-select-sm"
                        value={inputType}
                        onChange={(e) => handleUpdate('inputType', e.target.value)}
                    >
                        {TEXT_INPUT_SUBTYPES.map((type) => (
                            <option key={type} value={type}>
                                {formatInputType(type)}
                            </option>
                        ))}
                    </select>
                </PropsField>

                {['text', 'email', 'password', 'url', 'tel'].includes(inputType) && (
                    <PropsField label="Placeholder">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={question.placeholder || ''}
                            onChange={(e) => handleUpdate('placeholder', e.target.value)}
                        />
                    </PropsField>
                )}

                {['number', 'range'].includes(inputType) && (
                    <>
                        <div className="ecx-survey-props__row">
                            <PropsField label="Minimum value">
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={question.minValue ?? ''}
                                    onChange={(e) =>
                                        handleUpdate(
                                            'minValue',
                                            e.target.value ? parseFloat(e.target.value) : null
                                        )
                                    }
                                />
                            </PropsField>
                            <PropsField label="Maximum value">
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={question.maxValue ?? ''}
                                    onChange={(e) =>
                                        handleUpdate(
                                            'maxValue',
                                            e.target.value ? parseFloat(e.target.value) : null
                                        )
                                    }
                                />
                            </PropsField>
                        </div>
                        <PropsField label="Min error message">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={question.minErrorMessage || ''}
                                onChange={(e) => handleUpdate('minErrorMessage', e.target.value)}
                            />
                        </PropsField>
                        <PropsField label="Max error message">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={question.maxErrorMessage || ''}
                                onChange={(e) => handleUpdate('maxErrorMessage', e.target.value)}
                            />
                        </PropsField>
                    </>
                )}

                {['text', 'email', 'password'].includes(inputType) && (
                    <PropsField label="Maximum length">
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            value={question.maxLength ?? ''}
                            onChange={(e) =>
                                handleUpdate(
                                    'maxLength',
                                    e.target.value ? parseInt(e.target.value, 10) : null
                                )
                            }
                        />
                    </PropsField>
                )}
            </PropsSection>

            <PropsRequiredAndMeta
                required={question.required}
                onRequiredChange={(value) => handleUpdate('required', value)}
                questionId={questionId}
            />
        </PropsShell>
    );
};

export default TextBlockProperties;
